import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'children'> {
  id: string;
  label: ReactNode;
  helpText?: ReactNode;
  errorText?: ReactNode;
  trailing?: ReactNode;
  /** Visually hide the label (kept for screen readers). Mirrors Select. */
  hideLabel?: boolean;
  /** Borderless-until-hover/focus variant for inline-edit cells (table rows). */
  inline?: boolean;
  /** Always-visible bordered chrome for table inline-edit cells. */
  table?: boolean;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { id, label, helpText, errorText, trailing, className, hideLabel, inline, table, type = 'text', ...rest },
  ref
) {
  const helpId = helpText ? `${id}-help` : undefined;
  const errorId = errorText ? `${id}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div
      className={[
        'pn-field',
        table ? 'pn-field-table' : null,
        inline ? 'pn-field-inline' : null,
        className
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <label
        htmlFor={id}
        className={['pn-field-label', hideLabel ? 'pn-field-label-hidden' : null]
          .filter(Boolean)
          .join(' ')}
      >
        {label}
      </label>
      <input
        ref={ref}
        id={id}
        type={type}
        className="pn-field-input"
        aria-describedby={describedBy}
        aria-invalid={errorText ? true : undefined}
        {...rest}
      />
      {trailing}
      {helpText ? (
        <span id={helpId} className="pn-field-help">
          {helpText}
        </span>
      ) : null}
      {errorText ? (
        <span id={errorId} className="pn-field-error" role="alert">
          {errorText}
        </span>
      ) : null}
    </div>
  );
});
